import { createElement } from "react";
import {
  ElementFactory,
  type PanelModel,
  type Question,
  type QuestionCheckboxModel,
  type QuestionCommentModel,
  type QuestionDropdownModel,
  Serializer,
} from "survey-core";
import {
  type ISurveyCreator,
  ReactElementFactory,
  ReactQuestionFactory,
} from "survey-react-ui";
import { SurveyQuestionChrome } from "./elements/SurveyQuestionChrome";
import { SurveyPanel } from "./elements/SurveyPanel";
import { SurveyCheckboxQuestion } from "./questions/SurveyCheckboxQuestion";
import { SurveyCommentQuestion } from "./questions/SurveyCommentQuestion";
import { SurveyDatePickerQuestion } from "./questions/SurveyDatePickerQuestion";
import { SurveyDropdownQuestion } from "./questions/SurveyDropdownQuestion";
import { SurveyLicensePlateQuestion } from "./questions/SurveyLicensePlateQuestion";
import { SurveyTextQuestion } from "./questions/SurveyTextQuestion";
import {
  DatePickerQuestionModel,
  LicensePlateQuestionModel,
} from "./questions/models";
import { registerChoicesByUrl } from "./registerChoicesByUrl";
import { PostalCodeValidator } from "./validators/PostalCodeValidator";

let registered = false;

/**
 * Registers admin-v2's SurveyJS extensions: the custom question model
 * classes, the serializer properties the seeded schemas use, and the React
 * renderers that replace SurveyJS's default markup with admin-v2 primitives.
 *
 * Mirrors the shared package's `registerSurveyExtensions()`, but covers only
 * the types admin-v2 renders — importing the shared one would pull in its
 * `questions` barrel and with it antd, which is exactly what this module
 * avoids.
 *
 * Safe to call any number of times (module scope of several consumers, React
 * strict mode, multiple test files): the guard makes the whole body a no-op
 * after the first call.
 */
export const registerSurveyExtensions = (): void => {
  if (registered) return;
  registered = true;

  /* Model classes + serializer metadata */

  Serializer.addProperty("survey", {
    name: "intakeSupplement:boolean",
    default: true,
    category: "data",
  });
  Serializer.addProperty("survey", {
    name: "intakeTabs:boolean",
    default: true,
    category: "data",
  });

  // Custom question types of the seeded intake schemas, both plain
  // `question` subclasses (see the models for why `licensePlate` is not a
  // dropdown).
  Serializer.addClass(
    "datepicker",
    // See the `licensePlate` registration below: custom classes created via
    // `ElementFactory` only get the accessor for properties their own class
    // metadata owns, so `payloadPath` is re-declared per custom class.
    [{ name: "payloadPath", category: "data" }],
    () => new DatePickerQuestionModel(""),
    "question",
  );
  Serializer.addClass(
    "licensePlate",
    [
      { name: "country:string", default: "NL" },
      // Not redundant with the `question`-level registration below: custom
      // question classes created via `ElementFactory` do not get the
      // inherited property's own accessor (see the model), so the class
      // must carry `payloadPath` itself for the JSON value to land in the
      // property hash.
      { name: "payloadPath", category: "data" },
    ],
    () => new LicensePlateQuestionModel(""),
    "question",
  );

  // Seeded schemas tag questions with `payloadPath` (where the value lands in
  // the create-dossier payload) and dropdowns with `companyType` (client-side
  // narrowing of the mixed-type `/admin/companies/brief` list, applied in
  // `registerChoicesByUrl` before rows become choices). Without these
  // declarations SurveyJS would report the schema's own keys as unknown
  // properties on every model load. `companyType` is optional: absent means
  // "no narrowing" (the schema's `servicePartner` question relies on that).
  Serializer.addProperty("question", { name: "payloadPath", category: "data" });
  Serializer.addProperty("dropdown", {
    name: "companyType:string",
    category: "data",
  });
  // Present on the seeded schema; both are pass-throughs for us
  // (`autoCapitalize` maps to the input attribute, `showCount` to the
  // Textarea character counter when that lands) but must be declared so the
  // schema loads without unknown-property errors.
  Serializer.addProperty("text", {
    name: "autoCapitalize",
    category: "general",
    type: "string",
    default: "none",
    choices: ["none", "sentences", "words", "characters"],
  });
  Serializer.addProperty("comment", {
    name: "showCount:boolean",
    category: "general",
    default: false,
  });

  // The seeded schema uses the `postalcode` validator
  // (`{ "type": "postalcode", "country": "NL" }`). Registering only the
  // `postalcodevalidator` class is enough: SurveyJS resolves a validator's
  // JSON `type` by appending the "validator" class-name part, the same
  // convention as the built-in validators.
  Serializer.addClass(
    "postalcodevalidator",
    [{ name: "country", default: "NL" }],
    () => new PostalCodeValidator(),
    "surveyvalidator",
  );

  /* Model factories */

  ElementFactory.Instance.registerElement(
    "datepicker",
    (name) => new DatePickerQuestionModel(name),
  );
  ElementFactory.Instance.registerElement(
    "licensePlate",
    (name) => new LicensePlateQuestionModel(name),
  );

  /* Network plumbing */

  // Routes `choicesByUrl` fetches through admin-v2's authenticated,
  // tenant-scoped axios client and applies the `companyType` narrowing.
  // Prototype-level and idempotent, safe under the registration guard.
  registerChoicesByUrl();

  /* React renderers */

  // The factory props are SurveyJS-internal and untyped upstream, hence the
  // casts at the seam; the components themselves are fully typed. Factories
  // must `createElement` (not call the function) so each renderer mounts as a
  // real React component — the renderers use hooks
  // (`useSurveyQuestionRerender`) to re-render on model changes.
  ReactQuestionFactory.Instance.registerQuestion("text", (props: unknown) =>
    createElement(SurveyTextQuestion, props as { question: Question }),
  );
  ReactQuestionFactory.Instance.registerQuestion("comment", (props: unknown) =>
    createElement(
      SurveyCommentQuestion,
      props as {
        question: QuestionCommentModel;
      },
    ),
  );
  ReactQuestionFactory.Instance.registerQuestion("checkbox", (props: unknown) =>
    createElement(
      SurveyCheckboxQuestion,
      props as {
        question: QuestionCheckboxModel;
      },
    ),
  );
  ReactQuestionFactory.Instance.registerQuestion("dropdown", (props: unknown) =>
    createElement(
      SurveyDropdownQuestion,
      props as {
        question: QuestionDropdownModel;
      },
    ),
  );
  ReactQuestionFactory.Instance.registerQuestion(
    "datepicker",
    (props: unknown) =>
      createElement(
        SurveyDatePickerQuestion,
        props as {
          question: DatePickerQuestionModel;
        },
      ),
  );
  ReactQuestionFactory.Instance.registerQuestion(
    "licensePlate",
    (props: unknown) =>
      createElement(
        SurveyLicensePlateQuestion,
        props as {
          question: LicensePlateQuestionModel;
        },
      ),
  );

  // ReactElementFactory hands renderers `{ element, creator, survey, css }`
  // (see survey-react-ui's SurveyRowElement.createElement), so the panel and
  // question chrome take their model from `element`.
  ReactElementFactory.Instance.registerElement("panel", (props: unknown) =>
    createElement(
      SurveyPanel,
      props as { element: PanelModel; creator: ISurveyCreator },
    ),
  );
  ReactElementFactory.Instance.registerElement("question", (props: unknown) => {
    const { element, creator } = props as {
      element: Question;
      creator: ISurveyCreator;
    };
    return createElement(SurveyQuestionChrome, { question: element, creator });
  });
};
